import axios, { ApiResponse, ApiRoutes, RequestStatus } from "@/api/client";
import {
  createResource,
  deleteResource,
  getAllPages,
  getResource,
  getResourcePage,
  handleApiError,
  updateResource,
} from "@/api/helpers";
import {
  ExistingRecipient,
  ExistingSourceCredential,
  PaginationParams,
  PaginationResponse,
  Recipient,
  SourceCredential,
} from "@/types";

const getRecipients = async (
  paginationParams: PaginationParams,
  filters: Record<string, string> = {},
): Promise<PaginationResponse<ExistingRecipient>> =>
  getResourcePage<ExistingRecipient, { recipients: ExistingRecipient[] | null }>(
    ApiRoutes.RECIPIENTS,
    paginationParams,
    (response) => response.data.data.recipients ?? [],
    "Failed to fetch recipients.",
    filters,
  );

const getAllRecipients = async (): Promise<ExistingRecipient[]> => getAllPages(getRecipients);

const getRecipient = async (recipientID: ExistingRecipient["id"]): Promise<ExistingRecipient> =>
  getResource(
    `${ApiRoutes.RECIPIENTS}/${recipientID}`,
    (response) => response.data.data.recipient,
    "Failed to fetch recipient.",
  );

const patchRecipient = (recipientID: ExistingRecipient["id"], fields: Partial<Recipient>): Promise<ExistingRecipient> =>
  updateResource<Recipient, ExistingRecipient>(
    `${ApiRoutes.RECIPIENTS}/${recipientID}`,
    fields,
    "recipient",
    "recipient",
    "Failed to update recipient.",
  );

const deleteRecipient = (recipientID: ExistingRecipient["id"]): Promise<void> =>
  deleteResource(`${ApiRoutes.RECIPIENTS}/${recipientID}`, "Failed to delete recipient.");

const getSourceCredentials: () => Promise<ExistingSourceCredential[]> = () =>
  axios
    .get(ApiRoutes.RECIPIENT_SOURCE_CREDENTIALS)
    .then(
      (
        response: ApiResponse<{
          recipient_source_credentials: ExistingSourceCredential[];
        }>,
      ) => response.data.data.recipient_source_credentials,
    )
    .catch(handleApiError("Failed to fetch recipient source credentials.", true));

const testSourceCredentials: (c: SourceCredential) => Promise<RequestStatus> = (credential) =>
  axios
    .post(ApiRoutes.TEST_RECIPIENT_SOURCE_CREDENTIALS, {
      recipient_source_credential: credential,
    })
    .then((response: ApiResponse<RequestStatus>) => response.data.data)
    .catch(handleApiError("Source connection test failed.", true));

const postRecipient = (recipient: Recipient): Promise<ExistingRecipient> =>
  createResource<Recipient, ExistingRecipient>(
    ApiRoutes.RECIPIENTS,
    recipient,
    "recipient",
    "recipient",
    "Failed to post recipient.",
  );

const postSourceCredential = (sourceCredential: SourceCredential): Promise<ExistingSourceCredential> =>
  createResource<SourceCredential, ExistingSourceCredential>(
    ApiRoutes.RECIPIENT_SOURCE_CREDENTIALS,
    sourceCredential,
    "recipient_source_credential",
    "recipient_source_credential",
    "Failed to post source credential.",
  );

const RecipientsService = {
  getRecipients,
  getAllRecipients,
  getRecipient,
  getSourceCredentials,
  postRecipient,
  postSourceCredential,
  patchRecipient,
  testSourceCredentials,
  deleteRecipient,
};
export default RecipientsService;
